import { Flatfile } from '@flatfile/api-internal'
import { AppCreate, AppType } from '@flatfile/api-internal/api'
import {
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  IconName,
  Input,
  Label,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@flatfile/baseline'
import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import { useCollection } from '../../resources/hooks/useCollection'
import { useCreateResource, useUpdateResource } from '../../resources/hooks/useResourceMutation'
import getCollection from '../../resources/support/getCollection'
import { copyToClipboard } from '../../utils/copyToClipboard'
import { logger } from '../../utils/logging'
import DeveloperTipAlert from '../Shared/DeveloperAlertBox'
import { renderMappedIcon, validBaselineIcons } from '../Shared/IconMapper'

export const CreateNewAppForm = ({ autoBuild }: { autoBuild?: boolean }) => {
  const { environment } = useActiveEnvironment()
  const [updateAccount] = useUpdateResource('ACCOUNTS')
  const [submitDisabled, setSubmitDisabled] = useState(true)
  const [createApp] = useCreateResource('APPS')
  const currentApps = useCollection('APPS')
  const nav = useNavigate()
  const nameSpaceCopyRef = useRef<HTMLInputElement>(null)
  const [iconSearchTerm, setIconSearchTerm] = useState('')

  const newAppFormInitialValues: AppCreate = {
    name: '',
    namespace: '',
    type: 'CUSTOM',
    entity: 'Space',
    entityPlural: 'Spaces',
    icon: 'app',
    environmentFilters: {
      type: ['DEV'],
    },
  }

  // Custom form state
  const [formValues, setFormValues] = useState<Flatfile.AppCreate>(newAppFormInitialValues)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  const [prodChecked, setProdChecked] = useState(false)
  const [defaultChecked, setDefaultChecked] = useState(false)

  const updateFormValue = (field: string, value: any) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const validateForm = () => {
    const errors: Record<string, string> = {}

    // Validate name
    if (!formValues.name || formValues.name.trim().length === 0) {
      errors.name = 'Please enter a valid string'
    }

    // Validate namespace
    const namespaces = currentApps.map((app: Flatfile.App) => app.namespace)
    const unavailableNamespaces = ['portal', 'projects', 'workbooks', 'mapping', ...namespaces]

    if (!formValues.namespace || formValues.namespace.trim().length === 0) {
      errors.namespace = 'Select a namespace for your app'
    } else if (unavailableNamespaces.includes(formValues.namespace)) {
      errors.namespace = 'This namespace is reserved or already in use'
    } else {
      const existingNamespaces = getCollection('APPS')
        .getItems()
        .filter((app: Flatfile.App) => app.namespace === formValues.namespace)

      if (existingNamespaces.length > 0) {
        errors.namespace = 'This namespace is already in use'
      }
    }

    // Validate entity
    if (!autoBuild && (!formValues.entity || formValues.entity.trim().length === 0)) {
      errors.entity = 'Enter a valid string'
    }

    // Validate entityPlural
    if (!autoBuild && (!formValues.entityPlural || formValues.entityPlural.trim().length === 0)) {
      errors.entityPlural = 'Enter a valid string'
    }

    // Validate icon
    if (!formValues.icon || formValues.icon.length === 0) {
      errors.icon = 'Select an icon for your app'
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const addNewApp = async (values: Flatfile.AppCreate) => {
    try {
      const newApp = await createApp(values)
      if (!newApp) {
        throw new Error('Failed to create new app')
      }

      // Conditionally update the account only if there's exactly one app currently.
      if (currentApps.length === 0 || defaultChecked) {
        updateAccount({ defaultAppId: newApp.id })
      }

      toggleSubmitDisabled()

      getCollection('APPS').append(newApp) // Add the new app to a collection

      autoBuild ? nav(`/apps/custom/${newApp.id}/build/2`) : nav(`/apps/custom/${newApp.id}/start/`) // Navigate to the newly created app's page

      // Reset form
      setFormValues(newAppFormInitialValues)
      setFormErrors({})

      setSubmitDisabled(true) // Disable form submission until further interaction
      return newApp
    } catch (error) {
      logger.error('Failed to create new app', { error })
      throw new Error('Failed to create new app')
    }
  }

  const toggleSubmitDisabled = useCallback(() => {
    setSubmitDisabled(!submitDisabled)
  }, [submitDisabled])

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    updateFormValue('name', value)

    if (value !== '' && submitDisabled) {
      toggleSubmitDisabled()
    }

    let slug = value.trim().split(' ').join('-').toLowerCase()
    if (
      getCollection('APPS')
        .getItems()
        .filter((app: Flatfile.App) => app.namespace === slug).length > 0
    ) {
      slug += `-${currentApps.length}`
    }

    updateFormValue('namespace', slug)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    // Adjust the environmentFilters based on the prodChecked state
    const environmentTypes = ['DEV', ...(prodChecked ? ['PROD'] : [])]

    const updatedValues = {
      ...formValues,
      environmentFilters: {
        type: environmentTypes,
      },
      type: 'CUSTOM' as AppType,
      metadata: autoBuild ? { autobuild: true } : {},
    }

    const newApp = await addNewApp(updatedValues)
    if (autoBuild) {
      nav(`/apps/custom/${newApp.id}/build/`)
    } else {
      nav(`/apps/custom/${newApp.id}/start/`)
    }
  }

  // Filter icons based on search term
  const filteredIcons = Array.from(validBaselineIcons).filter((iconName) =>
    iconName.toLowerCase().includes(iconSearchTerm.toLowerCase())
  )

  return (
    <form onSubmit={handleSubmit} className='w-full'>
      <div className='grid grid-cols-12 gap-6'>
        <div
          className={cn(
            'col-span-12',
            // Only use lg:col-span-7 when the developer tip is shown
            !environment.isProd && !autoBuild ? 'lg:col-span-7' : 'lg:col-span-12'
          )}
        >
          <div className='mb-4'>
            <Label htmlFor='name'>Name</Label>
            <Input
              id='name'
              placeholder='Name for your app, eg: Product 1'
              value={formValues.name}
              onChange={handleNameChange}
            />
            {formErrors.name && (
              <Text variant='caption1' className='text-xs mt-1 text-red-500'>
                {formErrors.name}
              </Text>
            )}
          </div>

          <div className='mb-4'>
            <Label htmlFor='icon'>Custom Icon</Label>
            <div className='mt-1'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant='outline' className='w-full flex justify-between items-center'>
                    <div className='flex items-center gap-2'>
                      {formValues.icon ? (
                        <>
                          {renderMappedIcon({ iconName: formValues.icon as IconName, size: 16 })}
                          <span>{formValues.icon}</span>
                        </>
                      ) : (
                        <span className='text-text-default'>Select an icon</span>
                      )}
                    </div>
                    <Icon name='chevron-down-small' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className='max-h-[300px] overflow-y-auto w-[300px]'>
                  <div className='p-2'>
                    <Input
                      placeholder='Search icons...'
                      className='mb-2'
                      value={iconSearchTerm}
                      onChange={(e) => setIconSearchTerm(e.target.value)}
                    />
                  </div>
                  <div className='flex flex-col p-2 gap-1'>
                    {filteredIcons.map((iconName) => (
                      <DropdownMenuItem
                        key={iconName}
                        className='flex items-center py-2 px-3'
                        onClick={() => updateFormValue('icon', iconName)}
                      >
                        {renderMappedIcon({ iconName, size: 16 })}
                        <span className='ml-3 text-sm'>{iconName}</span>
                      </DropdownMenuItem>
                    ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            {formErrors.icon && (
              <Text variant='caption1' className='text-xs mt-1 text-red-500'>
                {formErrors.icon}
              </Text>
            )}
          </div>

          <div className='mb-4'>
            <Label htmlFor='namespace'>Namespace</Label>
            <div className='relative'>
              <Input
                id='namespace'
                ref={nameSpaceCopyRef}
                placeholder='A unique slug for your app'
                className={cn(autoBuild && 'opacity-75')}
                disabled={autoBuild}
                value={formValues.namespace}
                onChange={(e) => updateFormValue('namespace', e.target.value)}
              />
              {autoBuild ? (
                <button
                  type='button'
                  className='absolute right-2 top-1/2 -translate-y-1/2'
                  disabled={!formValues.name?.length}
                  onClick={() =>
                    copyToClipboard(nameSpaceCopyRef, { copy: (text: string) => navigator.clipboard.writeText(text) })
                  }
                >
                  <Icon name='copy-squares' size={16} />
                </button>
              ) : (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type='button' className='absolute right-2 top-1/2 -translate-y-1/2'>
                      <Icon name='circle-questionmark' size={16} className='text-muted' />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className='flex items-center px-2 py-1 z-[60]'>
                    <Text variant='body' className='text-sm'>
                      Namespace is auto-generated from the name of the App.
                    </Text>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            <Text variant='caption1' className='text-xs mt-1 text-gray-500'>
              {autoBuild
                ? 'This value will be the same as your App name'
                : 'Give your App a unique slug - developers will use this when event filtering.'}
            </Text>
            {formErrors.namespace && (
              <Text variant='caption1' className='text-xs mt-1 text-red-500'>
                {formErrors.namespace}
              </Text>
            )}
          </div>

          {!autoBuild && (
            <>
              <div className='mb-4'>
                <Label htmlFor='entity'>Entity</Label>
                <Input
                  id='entity'
                  placeholder="Your New App's Entity"
                  value={formValues.entity}
                  onChange={(e) => updateFormValue('entity', e.target.value)}
                />
                <Text variant='caption1' className='text-xs mt-1 text-gray-500'>
                  Give all the Spaces you make an easy-to-understand entity name.
                </Text>
                {formErrors.entity && (
                  <Text variant='caption1' className='text-xs mt-1 text-red-500'>
                    {formErrors.entity}
                  </Text>
                )}
              </div>

              <div className='mb-4'>
                <Label htmlFor='entityPlural'>Entity Plural</Label>
                <Input
                  id='entityPlural'
                  placeholder="Your New App's Entity Plural"
                  value={formValues.entityPlural}
                  onChange={(e) => updateFormValue('entityPlural', e.target.value)}
                />
                {formErrors.entityPlural && (
                  <Text variant='caption1' className='text-xs mt-1 text-red-500'>
                    {formErrors.entityPlural}
                  </Text>
                )}
              </div>
            </>
          )}

          <div className='my-6 space-y-2'>
            <Text variant='callout' className='font-medium'>
              App Settings
            </Text>

            {currentApps.length > 0 && (
              <div className='flex items-center gap-2'>
                <Checkbox
                  id='defaultApp'
                  checked={defaultChecked}
                  onCheckedChange={(checked) => setDefaultChecked(checked === true)}
                />
                <Label htmlFor='defaultApp' className='text-sm cursor-pointer'>
                  Set as Default App for Account
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type='button'>
                      <Icon name='circle-questionmark' size={16} className='text-muted' />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className='flex items-center px-2 py-1 z-[60] min-w-[200px]'>
                    <Text variant='body' className='text-sm'>
                      Select this option to set the App as your default landing page, and where all orphaned Spaces will
                      be consolidated. You can change this setting anytime.
                    </Text>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}

            <div className='flex items-center gap-2'>
              <Checkbox
                id='prodEnv'
                checked={prodChecked}
                onCheckedChange={(checked) => setProdChecked(checked === true)}
              />
              <Label htmlFor='prodEnv' className='text-sm cursor-pointer'>
                Enable visibility in Production Environments
              </Label>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type='button'>
                    <Icon name='circle-questionmark' size={16} className='text-muted' />
                  </button>
                </TooltipTrigger>
                <TooltipContent className='flex items-center px-2 py-1 z-[60]'>
                  <Text variant='body' className='text-sm'>
                    Ticking this box grants the App access across all Production Environments. You can undo this at any
                    time.
                  </Text>
                </TooltipContent>
              </Tooltip>
            </div>
          </div>

          <Button disabled={submitDisabled} className='w-full' type='submit'>
            {autoBuild ? 'Next' : 'Create New App'}
          </Button>
        </div>

        {!environment.isProd && !autoBuild && (
          <div className='col-span-12 lg:col-span-5'>
            <DeveloperTipAlert learnMoreUrl='https://flatfile.com/docs/guides/namespaces'>
              <Text variant='callout' className='text-[var(--flatfile-electric-6)]'>
                Take note of the{' '}
                <code className='px-1 py-0.5 rounded bg-[var(--flatfile-electric-1)] text-[var(--flatfile-electric-6)] font-semibold'>
                  namespace
                </code>
              </Text>
              <div className='p-1'></div>
              <Text className='text-xs text-[var(--flatfile-electric-6)]'>
                After creating this App, you'll want to setup a listener that is filtering for this specific namespace{' '}
                {formValues.namespace && (
                  <code className='px-1 py-0.5 rounded bg-[var(--flatfile-electric-1)] text-[var(--flatfile-electric-6)] font-semibold'>
                    space:{formValues.namespace}
                  </code>
                )}{' '}
                and is awaiting the{' '}
                <code className='px-1 py-0.5 rounded bg-[var(--flatfile-electric-1)] text-[var(--flatfile-electric-6)] font-semibold'>
                  ready
                </code>{' '}
                state of the{' '}
                <code className='px-1 py-0.5 rounded bg-[var(--flatfile-electric-1)] text-[var(--flatfile-electric-6)] font-semibold'>
                  space:configure
                </code>{' '}
                job.{' '}
              </Text>
              <Text className='text-xs text-[var(--flatfile-electric-6)]'>
                When a new {formValues.entity} is triggered in the <b>{formValues.name}</b> App, your listener will
                finalize the creation of this new {formValues.entity}.
              </Text>
            </DeveloperTipAlert>
          </div>
        )}
      </div>
    </form>
  )
}
